import { ComponentProps, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '~/components/Icon';
import Button from './Button';

export type IconButtonProps = {
  /**
   * The icon to display in the button.
   */
  icon: ComponentProps<typeof Icon>['icon'];
  /**
   * All icon buttons MUST have an accessible label for assistive technologies.
   */
  'aria-label': string;
} & Omit<ComponentProps<typeof Button>, 'children'>;

/**
 * IconButton component for icon-only buttons as a focused composition of a Button and Icon with best practices for accessibility enforced.
 * They also default to the `ghost` variant, as opposed to `filled` for regular Buttons.
 * Applies some styling overrides appropriate for icon-only buttons.
 *
 * Alternatively, for buttons that include both an icon and text, a Button can be used and compose an Icon within it along with content for the purposes of a prefix/suffix placement.
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { icon, 'aria-label': ariaLabel, className, variant = 'ghost', ...props },
    ref,
  ) => (
    <Button
      ref={ref}
      aria-label={ariaLabel} // guaranteed via typing
      data-component="corso-icon-button" // TODO consider if this is just a variant that's added to the current
      className={twMerge('aspect-square size-10', className)} // TODO revisit explicit size to be more responsive
      variant={variant}
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <Icon icon={icon} />
    </Button>
  ),
);
IconButton.displayName = 'IconButton';

export default IconButton;
